/*
 * Copyright (c) 2021 SARL SIGMA POLYNESIA
 */

import { Schema } from '@jalik/schema'
import React from 'react'
import { FormState, Values } from '@jalik/react-form'
import { parseField, ParseFieldOptions } from '@jalik/form-parser'
import logger from './logger'

export function createFieldInitializer<T> (schema: Schema) {
  return (name: string, form: FormState<T>) => {
    const props: React.ComponentProps<any> = {}
    try {
      const field = schema.getField(name)

      if (field != null) {
        props.max = field.getMax()
        props.min = field.getMin()
        props.pattern = field.getPattern()
        props.required = field.isRequired()
      }
      if (form?.modifiedFields[name]) {
        props.wrapperProps = { c: 'orange' }
      }
    } catch (error) {
      logger.error(`Cannot initialize field: ${error.message}`)
    }
    return props
  }
}

export function createFieldValidator<V extends Values> (schema: Schema) {
  return async (name: string, value: any, values: V): Promise<undefined | Error> => {
    try {
      schema.getField(name).validate(value, {
        context: values,
        rootOnly: true
      })
      return undefined
    } catch (error) {
      if (error instanceof Error) {
        return error
      }
    }
  }
}

export function createFormValidator<T extends Values> (schema: Schema, options?: Record<string, unknown>) {
  return (values: T) => Promise.resolve(
    schema.getErrors(values, { removeUnknown: true, ...options })
  )
}

/**
 * Execute callback with name when value change.
 * @param name
 * @param callback
 */
export function handleValueChange (name: string, callback: (name: string, value?: unknown) => void) {
  return (value: unknown) => {
    callback(name, value)
  }
}

/**
 * Returns a function that handle value change by returning an object with the
 * name and value.
 */
export function handleChangeEvent<T = unknown> (
  callback: (name: string, value: T, target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => void,
  options?: ParseFieldOptions
): (event: React.ChangeEvent) => void {
  return (event: React.ChangeEvent) => {
    const { target } = event

    if (target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLTextAreaElement) {
      const parsedValue = parseField(target, {
        nullify: true,
        trim: true,
        ...options
      })
      callback(target.name, parsedValue, target)
    }
  }
}

export function removeUnknownFields<T extends Record<string, unknown>> (fields: T, schema: Schema): T {
  return schema.removeUnknownFields(fields)
}

export function stringParser<T> (parser?: (value?: string) => T) {
  return (value?: string | number) => {
    if (value == null) {
      return null
    }
    if (parser == null) {
      return value
    }
    return parser(typeof value === 'string' ? value : String(value))
  }
}
